import { useEffect, useRef, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AudioButton, ExerciseCard, Muted, TextInput } from '@fluent/ui';
import type { ListenType as ListenTypeExercise } from '@fluent/types';
import type { MatchVerdict } from './matchAnswer.js';
import { useAudio } from '../audio/audioPlayer.js';
import './ListenType.css';

export interface ListenTypeProps {
  exercise: ListenTypeExercise;
  value: string;
  onChange: (value: string) => void;
  onEnter: () => void;
  locked: boolean;
  lastVerdict: MatchVerdict | null;
}

/**
 * ListenType — audio-only prompt, user types what they hear.
 *
 * Auto-plays the clip once on mount. Tapping the AudioButton replays
 * (or pauses if already playing). Phase 8: real TTS via R2 is now live, so
 * `translation_ka` is no longer a fallback for missing audio — it is the
 * post-answer meaning reveal, shown under the input once a verdict exists.
 * A learner who transcribed the sounds correctly still needs to be told
 * what the sentence means (same pattern as ListenChoose).
 */
export function ListenType({
  exercise,
  value,
  onChange,
  onEnter,
  locked,
  lastVerdict,
}: ListenTypeProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const audio = useAudio(exercise.audio_key);
  const autoPlayedRef = useRef<string | null>(null);

  useEffect(() => {
    const id = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.cancelAnimationFrame(id);
  }, [exercise.id]);

  // Auto-play once when the exercise becomes active.
  useEffect(() => {
    if (autoPlayedRef.current === exercise.id) return;
    autoPlayedRef.current = exercise.id;
    audio.play();
  }, [exercise.id, audio]);

  const onTapAudio = () => {
    if (audio.error) {
      audio.retry();
      return;
    }
    if (audio.playing) audio.stop();
    else audio.play();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onEnter();
    }
  };

  const showCloseHint = lastVerdict === 'close' && !locked;

  return (
    <ExerciseCard kicker={t('lesson.listen_type.kicker')}>
      <div className="fluent-listen">
        <div className="fluent-listen__audio-row">
          <AudioButton
            size="lg"
            playing={audio.playing}
            onClick={onTapAudio}
            data-testid="listen-audio-btn"
          />
          
          <p className="fluent-listen__hint">
            {audio.error
              ? t('lesson.listen_type.error')
              : audio.playing
                ? t('lesson.listen_type.playing')
                : t('lesson.listen_type.tap_to_play')}
          </p>
        </div>

        <TextInput
          ref={inputRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t('lesson.listen_type.placeholder')}
          aria-label={t('lesson.listen_type.aria_input')}
          disabled={locked}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          lang="en"
          data-testid="listen-type-input"
        />

        {lastVerdict != null && exercise.translation_ka && (
          <div className="fluent-listen__translation" data-testid="listen-type-translation">
            <Muted lang="ka">{exercise.translation_ka}</Muted>
          </div>
        )}

        {showCloseHint && (
          <p className="fluent-typed__nudge" role="status" aria-live="polite">
            {t('lesson.translate_typed.close_nudge')}
          </p>
        )}
      </div>
    </ExerciseCard>
  );
}
